import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useApi from "../hook/useApi";
import Loader from "./Loader/Loader";
import { formatCurrency, formatDateFull, getBrokerLabel } from "../utils/helper";

const Row = ({ label, children }) => (
  <div className="col-md-6 mb-16">
    <p className="text-secondary-light text-sm mb-4">{label}</p>
    <div className="text-md fw-medium mb-0">{children}</div>
  </div>
);

const FutBlock = ({ title, seg, masterSeg }) => {
  const isPerc = seg?.brokerageType === "percentage";
  const markupUnit = isPerc ? "%" : "₹";
  const userDel = Number(seg?.deliveryBrokerage) || Number(seg?.totalDeliveryBrokerage) || 0;
  const userIntra = Number(seg?.intradayBrokerage) || Number(seg?.totalIntradayBrokerage) || 0;

  const masterDel = Number(masterSeg?.deliveryBrokerage) || 0;
  const masterIntra = Number(masterSeg?.intradayBrokerage) || 0;

  const delMarkup = userDel - masterDel;
  const intraMarkup = userIntra - masterIntra;

  const totalDelPassOn = seg?.brokerSettings?.reduce((sum, b) => sum + (Number(b.deliveryPassOn) || 0), 0) || 0;
  const totalIntraPassOn = seg?.brokerSettings?.reduce((sum, b) => sum + (Number(b.intradayPassOn) || 0), 0) || 0;

  return (
    <div className="border radius-8 p-20 mb-20 bg-base">
      <h6 className="fw-semibold mb-16 text-primary-600">{title}</h6>
      {!seg || !seg.enabled ? (
        <span className="text-secondary-light">Disabled</span>
      ) : (
        <div className="row g-3">
          <Row label="Margin Limit">
            {formatCurrency(seg.marginLimit)}
          </Row>
          <Row label={`Delivery Brokerage (${isPerc ? "%" : "₹/Lot"})`}>
            <div className="fw-bold">{userDel} {markupUnit}</div>
            {masterSeg && <div className="text-info-main text-xs fw-bold mt-1">Admin Charge: {masterDel} {markupUnit}</div>}
          </Row>
          <Row label={`Intraday Brokerage (${isPerc ? "%" : "₹/Lot"})`}>
            <div className="fw-bold">{userIntra} {markupUnit}</div>
            {masterSeg && <div className="text-info-main text-xs fw-bold mt-1">Admin Charge: {masterIntra} {markupUnit}</div>}
          </Row>

          {masterSeg && (
            <div className="col-12 mt-4 mb-12">
              <div className="bg-info-focus text-info-600 border border-info-main py-10 px-16 rounded d-flex justify-content-between align-items-center shadow-sm">
                <div className="d-flex flex-column gap-1">
                  <h6 className="mb-0 fs-14 fw-bold">Broker Pass-On Summary</h6>
                  <p className="mb-0 text-xs fw-medium">
                    Markup: Del {delMarkup.toFixed(2)}{markupUnit} | Intra {intraMarkup.toFixed(2)}{markupUnit}
                  </p>
                </div>
                <div className="d-flex gap-4">
                  <div className="text-center px-12 border-end border-white border-opacity-25">
                    <span className="d-block text-xs text-uppercase fw-bold">Delivery Sum</span>
                    <span className="fw-bold">
                      {isPerc
                        ? `${totalDelPassOn.toFixed(1)}% / 100%`
                        : `${((totalDelPassOn * delMarkup) / 100).toFixed(2)}₹ / ${delMarkup.toFixed(2)}₹`
                      }
                    </span>
                  </div>
                  <div className="text-center px-12">
                    <span className="d-block text-xs text-uppercase fw-bold">Intraday Sum</span>
                    <span className="fw-bold">
                      {isPerc
                        ? `${totalIntraPassOn.toFixed(1)}% / 100%`
                        : `${((totalIntraPassOn * intraMarkup) / 100).toFixed(2)}₹ / ${intraMarkup.toFixed(2)}₹`
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {seg.brokerSettings?.map((bs, i) => (
            <React.Fragment key={i}>
              <Row label={`${bs.brokerId?.name || "Broker"} Delivery Pass-On`}>
                <span className="fw-bold">
                  {isPerc ? bs.deliveryPassOn : ((bs.deliveryPassOn * delMarkup) / 100).toFixed(2)} {markupUnit}
                </span>
                {!isPerc && <span className="text-xs text-secondary-light ms-1">({bs.deliveryPassOn}%)</span>}
              </Row>
              <Row label={`${bs.brokerId?.name || "Broker"} Intraday Pass-On`}>
                <span className="fw-bold">
                  {isPerc ? bs.intradayPassOn : ((bs.intradayPassOn * intraMarkup) / 100).toFixed(2)} {markupUnit}
                </span>
                {!isPerc && <span className="text-xs text-secondary-light ms-1">({bs.intradayPassOn}%)</span>}
              </Row>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

const UserPanelViewLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { AsyncGetApiCall } = useApi();
  const [loading, setLoading] = useState(true);
  const [u, setU] = useState(null);
  const [masterData, setMasterData] = useState(null);

  const fetchUser = async () => {
    setLoading(true);
    const result = await AsyncGetApiCall(`/users/view?userId=${id}`);
    if (result?.success && result?.data) {
      setU(result?.data);
      const masterId = result?.data?.createdById?._id || result?.data?.createdById;
      if (masterId) {
        const mResult = await AsyncGetApiCall(`/subadmin/get-subadmin/${masterId}`);
        if (mResult?.success && mResult?.data) {
          setMasterData(mResult?.data);
        }
      }
    } else {
      toast.error(result?.message || "User not found");
      setU(null);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchUser();
  }, [id]);

  if (loading) return <Loader />;
  if (!u) {
    return (
      <div className="card p-24 text-center">
        <h5 className="mb-16">User not found</h5>
        <button type="button" className="btn btn-primary" onClick={() => navigate("/users-list")}>Back to list</button>
      </div>
    );
  }

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-wrap justify-content-between align-items-center gap-2">
        <h5 className="mb-0">User Details: {u.name} ({u.loginId})</h5>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate("/users-list")}>Back</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate(`/edit-user-profile/${u._id}`)}>Edit</button>
        </div>
      </div>
      <div className="card-body p-24">
        <div className="row border-bottom mb-24 pb-8">
          <div className="col-12"><h6 className="text-primary-600 mb-16">Basic Information</h6></div>
          <Row label="Login ID">{u.loginId}</Row>
          <Row label="Name">{u.name}</Row>
          <Row label="Current Balance">{formatCurrency(u.currentBalance)}</Row>
          {(u?.unsettledPnl !== 0 && u?.unsettledPnl !== undefined) && (
            <Row label="Unsettled P&L">
              <span className={u?.unsettledPnl >= 0 ? 'text-success' : 'text-danger'}>
                {formatCurrency(u.unsettledPnl)}
              </span>
            </Row>
          )}
          <Row label="Mobile">{u.mobileNumber || "—"}</Row>
          <Row label="Account Type">{u.accountType?.name}</Row>
          <Row label="Loss Alert %">{u.lossAlert}%</Row>
          <Row label="Pending Order CMS">{u.pendingOrder}</Row>
          <Row label="Close Alert Margin">{formatCurrency(u.closeAlertMargin)}</Row>
          <Row label="Max Profit MTM">{formatCurrency(u.maxProfitMtm)}</Row>
          <Row label="Fresh Limit Allowed">{u.freshLimitAllowed ? "Yes" : "No"}</Row>
          <Row label="Auto Square">{u.autoSquare ? "Yes" : "No"}</Row>
          <Row label="Only Position">{u.onlyPosition ? "Yes" : "No"}</Row>
          <Row label="Status">{u.accountType?.isActive ? <span className="text-success">Active</span> : <span className="text-danger">Inactive</span>}</Row>
          <Row label="Created By">{`${u.createdByModel === 'Admin' ? u.createdByModel : `${u.createdById?.masterName || u.createdById?.name} (${u.createdById?.loginId}) (${u.createdByModel})`}` || "—"}</Row>
          <Row label="Created At">{formatDateFull(u.createdAt)}</Row>
          <div className="col-12"><Row label="Remarks">{u.remarks || "—"}</Row></div>
        </div>

        <div className="row mb-24">
          <div className="col-12"><h6 className="text-primary-600 mb-16">Broker Assignments</h6></div>
          <div className="col-12">
            <div className="table-responsive">
              <table className="table table-sm bordered-table mb-0">
                <thead>
                  <tr>
                    <th>Broker Name</th>
                    <th>Login ID</th>
                    <th>Partnership %</th>
                  </tr>
                </thead>
                <tbody>
                  {u.brokerDetails?.map((bd, i) => (
                    <tr key={i}>
                      <td>{bd.brokerId?.name || "N/A"}</td>
                      <td>{bd.brokerId?.loginId || "N/A"}</td>
                      <td>{bd.partnership}%</td>
                    </tr>
                  ))}
                  {(!u.brokerDetails || u.brokerDetails.length === 0) && (
                    <tr><td colSpan="3" className="text-center">No brokers assigned</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="row">
          {u.segmentSettings && Object.keys(u.segmentSettings).length > 0 ? (
            Object.entries(u.segmentSettings).map(([key, seg]) => {
              let formattedTitle = key.replace(/Details$/i, "").toUpperCase();
              if (formattedTitle.endsWith("FUT")) {
                formattedTitle = formattedTitle.replace(/FUT$/, "-FUT Settings");
              } else if (formattedTitle.endsWith("OPT")) {
                formattedTitle = formattedTitle.replace(/OPT$/, "-OPT Settings");
              } else {
                formattedTitle += " Settings";
              }

              return (
                <div className="col-md-6" key={key}>
                  <FutBlock title={formattedTitle} seg={seg} masterSeg={masterData?.segmentSettings?.[key]} />
                </div>
              );
            })
          ) : (
            <>
              <div className="col-md-6"><FutBlock title="NFO-FUT Settings" seg={u.nfoFutDetails} masterSeg={masterData?.segmentSettings?.nfoFutDetails} /></div>
              <div className="col-md-6"><FutBlock title="NFO-OPT Settings" seg={u.nfoOptDetails} masterSeg={masterData?.segmentSettings?.nfoOptDetails} /></div>
              <div className="col-md-6"><FutBlock title="MCX-FUT Settings" seg={u.mcxFutDetails} masterSeg={masterData?.segmentSettings?.mcxFutDetails} /></div>
              <div className="col-md-6"><FutBlock title="MCX-OPT Settings" seg={u.mcxOptDetails} masterSeg={masterData?.segmentSettings?.mcxOptDetails} /></div>
              <div className="col-md-6"><FutBlock title="NSE-FUT Settings" seg={u.nseFutDetails} masterSeg={masterData?.segmentSettings?.nseFutDetails} /></div>
              <div className="col-md-6"><FutBlock title="NSE-OPT Settings" seg={u.nseOptDetails} masterSeg={masterData?.segmentSettings?.nseOptDetails} /></div>
              <div className="col-md-6"><FutBlock title="CURRENCY Settings" seg={u.currencyDetails} masterSeg={masterData?.segmentSettings?.currencyDetails} /></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPanelViewLayer;
